<template>
    <div class="card mt-12">
        <div class="card-header" v-if="showNewButton || showSettingsBtn">
            <i
                class="fas fa-cogs pointer fa-2x float-right text-primary"
                v-if="showSettingsBtn"
                @click.stop="changeGridSettings"
                data-toggle="tooltip"
                :title="settingsTooltipText"
            ></i>

            <grid-user-settings
                ref="userSettings"
                :schemaModelFields="schemaModelFields"
                :componentName="componentName"
                :useLabelAsTitle="true"
                :showUserSettings="true"
                :gridOptions="userSettings"
                v-if="showUserSettingsForm"
                @saveGridSettings="saveGridSettings"
                @loadGridSettings="loadGridSettings"
                @userSettingsChanged="userSettingsChanged"
                @deleteGridSettings="deleteGridSettings"
            ></grid-user-settings>
        </div>

        <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
            <div class="form-inline pl-2 col-lg-6 col-md-12">
                <button
                    value="hasCreateRights"
                    type="button"
                    class="btn btn-primary"
                    @click.stop="createBtnClick"
                >{{ $t('buttons.create.btnText_a') }}</button>
            </div>

            <div class="input-group col-lg-6 col-md-12">
                <input
                    v-model="searchQuery"
                    v-on:keyup.enter.stop="massSearch"
                    v-on:keyup.esc.stop="clearFilters"
                    type="text"
                    class="form-control"
                    aria-label="Mass search input"
                    aria-describedby="massSearch"
                />
                <button type="button" class="btn btn-secondary" @click.stop="massSearch">
                    <i class="fas fa-search"></i>
                    {{ $t('buttons.search.btnText') }}
                </button>
                <button type="button" class="btn btn-secondary" @click.stop="clearFilters">
                    <i class="fas fa-times"></i>
                    {{ $t('buttons.clearFilters.btnText') }}
                </button>
            </div>
        </div>

        <div class="card-body">
            <kendo-datasource
                ref="datasource1"
                :transport="transportSettings"
                :schema-model-id="'id'"
                :schema-model-fields="schemaModelFields"
                :schema-total="'total'"
                :schema-data="'data'"
                :page-size="defaultGridRows"
                :server-paging="true"
                :server-sorting="true"
                :server-filtering="true"
                :error="dataSourceError"
                :requestEnd="datasorceRequestEnd"
            ></kendo-datasource>

            <kendo-grid
                ref="grid"
                :id="'personsGrid'"
                :data-source-ref="'datasource1'"
                :pageable="gridOptions.pageable"
                :navigatable="gridOptions.navigatable"
                :editable="gridOptions.editable"
                :filterable="gridOptions.filterable"
                :reorderable="gridOptions.reorderable"
                :resizable="gridOptions.resizable"
                :groupable="gridOptions.groupable"
                :allow-copy="gridOptions.allowCopy"
                :column-menu="gridOptions.columnMenu"
                :sortable-mode="gridOptions.sortable.mode"
                :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                :sortable-show-indexes="gridOptions.sortable.showIndexes"
                :selectable="gridOptions.selectable"
                :scrollable="gridOptions.scrollable"
                :toolbar="toolbar"
                :excel="gridOptions.excel"
                :pdf="gridOptions.pdf"
                :dataBound="dataBound"
                :persistSelection="true"
                :pdfExport="beforePdfExport"
            >
                <kendo-grid-column :selectable="true" :width="60" :lockable="true" :locked="true"></kendo-grid-column>
                <kendo-grid-column
                    :title="$t('actions')"
                    field="btnsColumn"
                    :template="btnsTemplate"
                    :sortable="false"
                    :filterable="false"
                    :lockable="true"
                    :locked="true"
                    :width="150"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'fullName'"
                    :width="200"
                    :lockable="true"
                    :locked="true"
                    :title="$t('person.gridHeaders.fullname')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'id'"
                    :width="100"
                    :hidden="true"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.id')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'username'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.username')"
                ></kendo-grid-column>

                <kendo-grid-column
                    :field="'firstName'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.firstName')"
                    :hidden="true"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'middleName'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.middleName')"
                    :hidden="true"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'lastName'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.lastName')"
                    :hidden="true"
                ></kendo-grid-column>

                <kendo-grid-column
                    :field="'identificationType'"
                    :width="100"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.identificationType')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'identificationNumber'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.identification')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'phone'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.phone')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'mobilePhone'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.mobilePhone')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'email'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.email')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'institutionRegister'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.institution')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'institutionRegisterLocation'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.institutionLocation')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'position'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="isEnVersion"
                    :title="$t('person.position')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'positionEn'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="!isEnVersion"
                    :title="$t('person.position')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'description'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.description')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'gender'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="isEnVersion"
                    :title="$t('person.gender')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'genderEn'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="!isEnVersion"
                    :title="$t('person.gender')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'titleDegree'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="isEnVersion"
                    :title="$t('person.titleDegree')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'titleDegreeEn'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="!isEnVersion"
                    :title="$t('person.titleDegree')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'educationDegree'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="isEnVersion"
                    :title="$t('person.educationDegree')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'educationDegreeEn'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :hidden="!isEnVersion"
                    :title="$t('person.educationDegree')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'hasParent'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.hasParent')"
                    :template="hasParentCellTemplate"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'parentId'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.parentId')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'parentFullName'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.parent')"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'hasChanges'"
                    :width="150"
                    :lockable="true"
                    :locked="false"
                    :title="$t('person.gridHeaders.hasChanges')"
                    :template="hasChangesCellTemplate"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'updatedAtUtc'"
                    :title="$t('person.gridHeaders.updatedAtUtc')"
                    :width="150"
                    :format="customDatetimeFormat"
                ></kendo-grid-column>
                <kendo-grid-column
                    :field="'updatedByUsername'"
                    :title="$t('person.gridHeaders.updatedBy')"
                    :width="150"
                ></kendo-grid-column>
            </kendo-grid>
        </div>

        <table class="table table-sm">
            <tbody>
                <tr>
                    <td class="table-success font-weight-bold text-center">Има обвързан потребител</td>
                    <td class="table-info font-weight-bold text-center">Има родителска личност</td>
                </tr>
            </tbody>
        </table>

        <CreateModal :show="showCreate" @onCreate="onCreate" @onClose="onCreateClose"></CreateModal>
        <EditModal
            v-model="personModel"
            :show="showEdit"
            @onClose="onEditClose"
            @onSave="onPersonEdit"
        ></EditModal>
        <DetailsModal
            v-model="personModel"
            :show="showDetails"
            :allowEdit="hasUpdateRights"
            @onEdit="onEditClicked"
            @onClose="onDetailsClose"
        ></DetailsModal>

        <PersonMergeModal
            :selectedIds="selectedRowsIds"
            :show.sync="showPersonsMerge"
            :grid="selectedRowsGrid"
            @save="onSelectedRowsMerge"
        ></PersonMergeModal>
    </div>
</template>

<script>
import Vue from "vue";
import JSZip from "jszip";
import BtnsTemplate from "../../components/grid/StandardBtnsTemplate";
import GridUserSettings from "../../components/grid/UserSettings";
import EditModal from "../../components/person/edit.modal";
import DetailsModal from "../../components/person/details.modal";
import CreateModal from "../../components/person/create.modal";
import personService from "../../services/person.service";
import personalizationService from "../../services/personalization.service";
import PersonMergeModal from "../../components/administration/person/personMerge.modal";
    import { KendoGridPageSize } from '../../models/enums';
import { mapGetters } from "vuex";

/*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
kendo.pdf.defineFont({
    "DejaVu Sans":
        "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
    "DejaVu Sans|Bold":
        "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
    "DejaVu Sans|Bold|Italic":
        "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
    "DejaVu Sans|Italic":
        "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
    WebComponentsIcons:
        "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf",
});

export default {
    components: {
        "grid-user-settings": GridUserSettings,
        EditModal,
        DetailsModal,
        CreateModal,
        PersonMergeModal,
    },
    props: {
        // Пропъртита, които трабват на компонента с бутоните за дашборд настройките
        showHideButton: {
            default: false,
            type: Boolean,
        },
        showPinButton: {
            default: true,
            type: Boolean,
        },
        showNewButton: {
            default: true,
            type: Boolean,
        },
        showResizeIcon: {
            default: false,
            type: Boolean,
        },
        size: {
            default: 6,
            type: Number,
        },
        showBtns: {
            default: true,
            type: Boolean,
        },
        showSettingsBtn: {
            default: true,
            type: Boolean,
        },
        showHeader: {
            default: true,
            type: Boolean,
        },
    },
    data() {
        return {
            componentName: "personsListGrid", // Трабва на конпонентата за бутоните за дашборд настройките
            titleKey: "person.gridTitle", // Трабва на конпонентата за бутоните за дашборд настройките
            defaultGridRows: this.$store.getters.defaultGridRows,
            settingsTooltipText: this.$t("buttons.settings.tooltipText"),
            customSettingsLoaded: false,
            schemaModelFields: {
                id: {
                    name: "id",
                    type: "number",
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: "person.gridHeaders.id",
                },
                firstName: {
                    name: "firstName",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                    validation: { required: true, type: "text" },
                },
                middleName: {
                    name: "middleName",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                lastName: {
                    name: "lastName",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                    validation: { required: true, type: "text" },
                },
                fullName: {
                    name: "fullName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                identificationType: {
                    name: "identificationType",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                identificationNumber: {
                    name: "identificationNumber",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                phone: {
                    name: "phone",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                mobilePhone: {
                    name: "mobilePhone",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                email: {
                    name: "email",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                    validation: { required: true, type: "email" },
                },
                institutionRegister: {
                    name: "institutionRegister",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                institutionRegisterLocation: {
                    name: "institutionRegisterLocation",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                position: {
                    name: "position",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                positionEn: {
                    name: "positionEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                description: {
                    name: "description",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                gender: {
                    name: "gender",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                genderEn: {
                    name: "genderEn",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                hasParent: {
                    name: "hasParent",
                    type: "boolean",
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: "registers.organizers.gridHeaders.active",
                },
                parentId: {
                    name: "parentId",
                    type: "number",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                parentFirstName: {
                    name: "parentFirstName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                parentMiddleName: {
                    name: "parentMiddleName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                parentLastName: {
                    name: "parentLastName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                parentFullName: {
                    name: "parentFullName",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                username: {
                    name: "username",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                hasChanges: {
                    name: "hasChanges",
                    type: "boolean",
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: "registers.organizers.gridHeaders.active",
                },
                educationDegree: {
                    name: "educationDegree",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                educationDegreeEn: {
                    name: "educationDegreeEn",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                titleDegree: {
                    name: "titleDegree",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                titleDegreeEn: {
                    name: "titleDegreeEn",
                    type: "string",
                    editable: true,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                updatedAtUtc: {
                    name: "updatedAtUtc",
                    type: "date",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
                updatedByUsername: {
                    name: "updatedByUsername",
                    type: "string",
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: "person",
                },
            },
            transportSettings: {
                read: {
                    url:
                        this.$store.getters.baseUrl + "/api/person/listDetails",
                    dataType: "json",
                    type: "POST",
                    contentType: "application/json",
                    beforeSend: this.beforeSend,
                },
                parameterMap: this.parameterMap,
            },
            gridOptions: {
                ...this.$store.getters.gridOptions,
                editable: {
                    mode: "popup" /* inline, popup */,
                    window: {
                        title: this.$t("person.gridTitle"),
                        animation: true,
                    },
                    move: true,
                },
                excel: {
                    fileName: "Persons.xlsx",
                    allPages: false,
                    filterable: true,
                    collapsible: true,
                },
                pdf: {
                    fileName: "Persons.pdf",
                    title: this.$t(this.titleKey),
                    subject: this.$t(this.titleKey),
                    creator: this.$store.userName,
                    allPages: false,
                    avoidLinks: true,
                    paperSize: "A4",
                    margin: {
                        top: "2cm",
                        left: "1cm",
                        right: "1cm",
                        bottom: "1cm",
                    },
                    landscape: true,
                    repeatHeaders: true,
                    scale: 0.6,
                },
            },
            working: false,
            showUserSettingsForm: false,
            searchQuery: "",
            showEdit: false,
            personModel: null,
            showDetails: false,
            showCreate: false,
            showPersonsMerge: false,
            selectedRowsIds: null,
            selectedRowsGrid: null,
        };
    },
    methods: {
        dataBound(e) {
            if (!this.customSettingsLoaded) {
                this.customSettingsLoaded = true;
                this.loadGridSettings();
            }

            var items = e.sender.items();
            items.each(function (idx, item) {
                let dataItem = e.sender.dataItem(item);
                if (dataItem.username) {
                    $(item).addClass("left-border-green");
                } else if (dataItem.hasParent) {
                    $(item).addClass("left-border-blue");
                }
            });
        },
        datasorceRequestEnd(e) {
            if (e.type === "create" && e.response) {
                this.refresh();
            } else if (e.type === "update" && e.response) {
                this.refresh();
            } else if (e.type === "destroy" && e.response) {
                this.refresh();
            }
        },
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t("notifications.error"),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: "danger",
                time: this.$store.getters.notificationDuration,
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== "read" && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                // Викаме POST action за листване на номенклатурите и трябва да сериализираме
                // options. Така се предава информация за take, skip , page и списък с обекти
                // описващи сортирането
                return kendo.stringify(options);
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    parentComponent: this.$refs.grid,
                    btns: [
                        {
                            name: "details",
                            text: "",
                            tooltip: this.$t("buttons.details.btnText"),
                            class: "btn btn-primary",
                            icon: "fas fa-eye",
                            show: this.hasReadRights,
                            clickHandler: this.detailsBtnClick,
                        },
                        {
                            name: "edit",
                            text: "",
                            tooltip: this.$t("buttons.edit.btnText"),
                            class: "btn btn-primary",
                            icon: "fas fa-edit",
                            show: this.hasUpdateRights,
                            clickHandler: this.editBtnClick,
                        },
                        {
                            name: "delete",
                            text: "",
                            tooltip: this.$t("buttons.delete.btnText"),
                            class: "btn btn-danger",
                            icon: "fas fa-times",
                            show: this.hasDeleteRights,
                            clickHandler: this.deleteBtnClick,
                        },
                        {
                            name: "unmerge",
                            text: "",
                            tooltip: this.$t("buttons.unmerge.btnText"),
                            class: "btn btn-danger",
                            icon: "far fa-object-ungroup",
                            show: this.hasMergeRights && e.hasParent,
                            clickHandler: this.unmergeBtnClick,
                        },
                    ],
                },
            };
        },
        detailsBtnClick(args) {
            if (!this.hasReadRights) {
                return this.$router.push(`/unauthorized`);
            }

            return this.$router.push(`/account/${args.id}`);
        },
        editBtnClick(args) {
            if (!this.hasUpdateRights) {
                return this.$router.push(`/unauthorized`);
            }

            this.showEditModal(args.id);
        },
        deleteBtnClick(args) {
            kendo
                .confirm(
                    `${this.$t("person.confirmations.delete")} "${
                        args.fullName
                    }"?`
                )
                .then(
                    function () {
                        personService
                            .delete(args.id)
                            .then((response) => {
                                this.refresh();

                                this.$vs.notify({
                                    title: response.request
                                        ? this.$t(
                                              "request.response.status." +
                                                  response.request.status
                                          )
                                        : this.$t(
                                              "request.response.status.200"
                                          ),
                                    text: response.data.message
                                        ? response.data.message
                                        : response.request.statusText
                                        ? response.request.statusText
                                        : response.message,
                                    color: "success",
                                    time: this.$store.getters
                                        .notificationDuration,
                                });
                            })
                            .catch((error) => {
                                this.$vs.notify({
                                    title: this.$t(
                                        "request.response.status." +
                                            this.$helpers.errorStatusCode(error)
                                    ),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters
                                        .notificationDuration,
                                });
                            });
                    }.bind(this)
                );
        },
        createBtnClick() {
            if (!this.hasCreateRights) {
                return this.$router.push(`/unauthorized`);
            }

            this.showCreateModal();
        },
        unmergeBtnClick(args) {
            kendo
                .confirm(this.$t('administration.person.confirmations.unmerge', { person: args.fullName }))
                .then(
                    function () {
                        personService
                            .unmerge(args.id)
                            .then((response) => {
                                this.refresh();

                                this.$vs.notify({
                                    title: this.$t(
                                        "buttons.mergeSelected.btnText"
                                    ),
                                    text: this.$helpers.responseMessage(
                                        response
                                    ),
                                    color: "success",
                                    time: this.$store.getters
                                        .notificationDuration,
                                });
                            })
                            .catch((error) => {
                                this.$vs.notify({
                                    title: this.$t(
                                        "buttons.mergeSelected.btnText"
                                    ),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters
                                        .notificationDuration,
                                });
                            });
                    }.bind(this)
                );
        },
        getGridOptions() {
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                return grid.getOptions();
            }
        },
        saveGridSettings() {
            const gridSettings = this.getGridOptions();
            const grid = this.$refs.grid.kendoWidget();

            if (gridSettings && grid) {
                this.$refs.userSettings.setGridSettings(gridSettings);

                personalizationService
                    .saveGridSettings(this.componentName, gridSettings)
                    .then((response) => {
                        this.$vs.notify({
                            title: this.$t(
                                "request.response.status." +
                                    this.$helpers.responseStatusCode(response)
                            ),
                            text: this.$helpers.responseMessage(response),
                            color: "success",
                            time: this.$store.getters.notificationDuration,
                        });
                    })
                    .catch((error) => {
                        this.$vs.notify({
                            title: this.$t(
                                "request.response.status." +
                                    this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration,
                        });
                    });
            }
        },
        loadGridSettings() {
            personalizationService
                .loadGridSettings(this.componentName)
                .then((response) => {
                    try {
                        var options = personalizationService.mergePersonGridSettings(
                            this.getGridOptions(),
                            response.data.settings
                        );

                        let grid = this.$refs.grid.kendoWidget();
                        if (options && grid) {
                            grid.setOptions(options);

                            if (
                                options.pageable.size !==
                                grid.dataSource.options.pageSize
                            ) {
                                grid.dataSource.pageSize(options.pageable.size);
                                grid.refresh();
                            }

                            const userSettingsEditor = this.$refs.userSettings;
                            if (userSettingsEditor) {
                                userSettingsEditor.setGridSettings(
                                    grid.getOptions()
                                );
                            }
                        }

                        this.customSettingsLoaded = true;
                    } catch (e) {
                        this.customSettingsLoaded = true;
                    }
                })
                .catch((error) => {
                    this.customSettingsLoaded = true;

                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        deleteGridSettings() {
            personalizationService
                .delteGridSettings(this.componentName)
                .then((response) => {
                    this.$router.go();
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        userSettingsChanged(userSettings, pageSizeChanged) {
            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                try {
                    grid.setOptions(userSettings);
                    if (userSettings.pageable.size === KendoGridPageSize.All) {
                        grid.dataSource.pageSize(
                            this.$refs["datasource"].kendoDataSource.total()
                        );
                    } else {
                        grid.dataSource.pageSize(userSettings.pageable.size);
                    }
                    grid.refresh();
                } catch (e) {}
            }
        },
        hasParentCellTemplate: function (dataItem) {
            let templateHtml =
                '<div class="text-center"><span class="badge ' +
                (dataItem.hasParent ? "badge-warning" : "badge-secondary") +
                '">' +
                kendo.htmlEncode(
                    dataItem.hasParent ? this.$t("yes") : this.$t("no")
                ) +
                "</span></div>";

            return templateHtml;
        },
        hasChangesCellTemplate: function (dataItem) {
            let templateHtml =
                '<div class="text-center"><span class="badge ' +
                (dataItem.hasChanges ? "badge-success" : "badge-secondary") +
                '">' +
                kendo.htmlEncode(
                    dataItem.hasChanges ? this.$t("yes") : this.$t("no")
                ) +
                "</span></div>";

            return templateHtml;
        },
        beforeSend: function (req) {
            if (this.token) {
                req.setRequestHeader("Authorization", "Bearer " + this.token);
            }
        },
        changeGridSettings() {
            this.showUserSettingsForm = !this.showUserSettingsForm;
        },
        // Трабва на конпонентата за бутоните за дашборд настройките. Предава събитието от компонента с бутоните до началната страница(дашборда)
        resize(componentName, size) {
            this.$emit(
                "resize",
                componentName,
                size,
                this.$refs.grid.kendoWidget()
            );
        },
        // Трабва на конпонентата за бутоните за дашборд настройките. Предава събитието от компонента с бутоните до началната страница(дашборда)
        hide(componentName) {
            this.$emit("hide", componentName);
        },
        // Трабва на конпонентата за бутоните за дашборд настройките. Предава събитието от компонента с бутоните до началната страница(дашборда)
        pinned(componentName) {
            this.$emit("pinned", componentName);
        },
        // Трабва на конпонентата за бутоните за дашборд настройките. Предава събитието от компонента с бутоните до началната страница(дашборда)
        unpinned(componentName) {
            this.$emit("unpinned", componentName);
        },
        refresh() {
            const ds = this.$refs.datasource1.kendoWidget();
            if (ds) {
                ds.read();
            }
        },
        clearFilters() {
            this.searchQuery = "";
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.sort({});
                grid.dataSource.page(1);
                grid.dataSource.aggregate([]);
                grid.dataSource.filter({});
            }
        },
        massSearch() {
            if (!this.searchQuery) {
                this.$vs.notify({
                    title: "Search",
                    text: "Nothing to search for",
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });

                return;
            }

            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                let filter = this.$helpers.getMassFilterForGrid(
                    grid,
                    this.searchQuery
                );
                if (filter) {
                    grid.dataSource.filter(filter);
                }
            }
        },
        showDetailsModal(id) {
            personService
                .getById(id)
                .then((response) => {
                    let model = response.data;
                    if (model) {
                        this.personModel = model;
                        this.showDetails = true;
                    } else {
                        this.editModel = null;
                        this.showDetails = true;
                    }
                })
                .catch((error) => {
                    this.personModel = null;
                    this.showDetails = false;

                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        showEditModal(id) {
            personService
                .getById(id)
                .then((response) => {
                    let model = response.data;
                    if (model) {
                        this.personModel = model;
                        this.showEdit = true;
                    } else {
                        this.editModel = null;
                        this.showEdit = false;
                    }
                })
                .catch((error) => {
                    this.personModel = null;
                    this.showEdit = false;

                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        showCreateModal() {
            this.showCreate = true;
        },
        onCreateClose() {
            this.showCreate = false;
        },
        onCreate() {
            this.refresh();
        },
        onEditClose() {
            this.showEdit = false;
            this.personModel = null;
        },
        onDetailsClose() {
            this.showDetails = false;
            this.personModel = null;
        },
        onPersonEdit(item) {
            this.refresh();
        },
        onEditClicked(item) {
            this.showEditModal(item.id);
        },
        beforePdfExport(e) {
            e.preventDefault();

            if (this.hasCreateRights || this.hasUpdateRights) {
                this.mergeSelected(e);
            }
        },
        mergeSelected(e) {
            const selectedIds = this.$helpers.getGridSelectedRowsIds(e.sender);
            if (!selectedIds || selectedIds.length <= 0) {
                this.$vs.notify({
                    title: this.$t("buttons.edit.btnText"),
                    text: this.$t("administration.person.emptySelection"),
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });
                return;
            }

            this.selectedRowsIds = selectedIds;
            this.selectedRowsGrid = e.sender;
            this.showPersonsMerge = true;
        },
        onSelectedRowsMerge(grid) {
            if (grid) {
                grid.clearSelection();
                grid.dataSource.read();
            }

            this.selectedRowsIds = [];
            this.selectedRowsGrid = null;
        },
    },
    mounted: function () {},
    created: function () {
        window.JSZip = JSZip; // Необходимо е за да работи експорта в excel
    },
    computed: {
        ...mapGetters(["dateTimeFormat", "isEnVersion"]),
        customDatetimeFormat() {
            return `{0:${this.dateTimeFormat}}`;
        },
        token: function () {
            return this.$store.getters.authToken;
        },
        userSettings() {
            let grid = this.$refs.grid.kendoWidget();
            return grid.getOptions();
        },
        hasReadRights() {
            return (
                this.$store.getters.claims.person.read ||
                this.$store.getters.claims.aspNetUser.read
            );
        },
        hasCreateRights() {
            return (
                this.$store.getters.claims.person.create ||
                this.$store.getters.claims.aspNetUser.create
            );
        },
        hasUpdateRights() {
            return (
                this.$store.getters.claims.person.update ||
                this.$store.getters.claims.aspNetUser.update
            );
        },
        hasDeleteRights() {
            return (
                this.$store.getters.claims.person.delete ||
                this.$store.getters.claims.aspNetUser.delete
            );
        },
        hasMergeRights() {
            return this.$store.getters.claims.aspNetUser.create || this.$store.getters.claims.aspNetUser.update || this.$store.getters.claims.aspNetUser.delete;
        },
        toolbar: function () {
            let btns = [
                {
                    name: "excel",
                    text: this.$t("buttons.excelExport.tooltipText"),
                },
            ];

            if (this.hasMergeRights) {
                btns.push({
                    name: "pdf",
                    text: this.$t("buttons.mergeSelected.btnText"),
                    iconClass: "k-icon k-i-cells-merge",
                });
            }

            return btns;
        },
        command: function () {
            return [
                { name: "edit", text: "" },
                { name: "destroy", text: "" },
            ];
        },
    },
};
</script>

<style scoped>
.btn {
    margin-right: 10px;
    margin-left: 5px;
}

.checkbox {
    margin-right: 5px;
}

.selectFile {
    display: none;
}

.inlineForm {
    display: inline;
}

/*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
.k-pdf-export .k-grid {
    font-family: "DejaVu Sans", "Arial", sans-serif;
}

.k-pdf-export .k-filterable .k-grid-filter {
    display: none;
}

.k-pdf-export .k-with-icon .k-header-column-menu {
    display: none;
}

.k-grid .k-grid-header .k-header .k-link {
    height: auto;
}

.k-grid .k-grid-header .k-header {
    white-space: normal;
}

.k-grid-edit,
.k-grid-details,
.k-grid-delete,
.k-grid-pdf,
.k-grid-excel {
    padding: 0;
    margin-bottom: 2px;
}
</style>

<style lang="scss">
tr.left-border-danger {
    &:hover {
        color: rgba(255, 0, 0, 0.7) !important;
    }

    td:first-child {
        border-left: 5px solid rgba(255, 0, 0, 0.5) !important;
    }
}

tr.left-border-blue {
    &:hover {
        color: #3498db !important;
    }

    td:first-child {
        border-left: 5px solid #3498db !important;
    }
}

tr.left-border-green {
    &:hover {
        color: #4caf50 !important;
    }

    td:first-child {
        border-left: 5px solid #4caf50 !important;
    }
}
</style>
